import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useLabels } from '@/app/workspace';
import { SUGGESTED_LABELS } from '@/lib/labels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form';
import { Tag } from '@/components/ui/system';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlay';

export function ContactLabelEditor({
  labels,
  disabled,
  onSave,
}: {
  labels: string[];
  disabled: boolean;
  onSave: (labels: string[]) => Promise<unknown>;
}) {
  const known = useLabels();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const pool = [
    ...new Map([...(known.data || []), ...SUGGESTED_LABELS].map((l) => [l.toLowerCase(), l])).values(),
  ];
  const available = pool.filter((l) => !labels.some((v) => v.toLowerCase() === l.toLowerCase()));
  const options = available.filter((l) => l.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 8);
  const add = async (raw: string) => {
    const value = raw.trim().slice(0, 40);
    if (!value || labels.some((l) => l.toLowerCase() === value.toLowerCase())) return;
    try {
      await onSave([...labels, value]);
      setQuery('');
      setOpen(false);
    } catch {
      /* Keep picker open to retry. */
    }
  };
  return (
    <div className="contact-label-editor flex flex-wrap gap-2 items-center">
      {labels.map((l) => (
        <Tag key={l}>
          {l}
          <button
            className="label-remove"
            aria-label={`Remove ${l}`}
            disabled={disabled}
            onClick={() => {
              void onSave(labels.filter((v) => v !== l)).catch(() => {});
            }}
          >
            <X size={12} />
          </button>
        </Tag>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button className="label-add-chip" size="xs" variant="secondary" disabled={disabled}>
            <Plus size={12} />
            Add label
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <Input
            aria-label="Find or create a label"
            placeholder="Find or create a label"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void add(query || options[0] || '');
              }
            }}
          />
          <div className="mt-2 space-y-1">
            {options.map((l) => (
              <button
                className="contact-label-option"
                key={l}
                disabled={disabled}
                onClick={() => void add(l)}
              >
                {l}
              </button>
            ))}
            {query.trim() && !pool.some((l) => l.toLowerCase() === query.trim().toLowerCase()) && (
              <button className="contact-label-option" disabled={disabled} onClick={() => void add(query)}>
                <Plus size={14} />
                Create “{query.trim()}”
              </button>
            )}
            {!options.length && !query && (
              <p className="text-xs text-fg-muted p-2">Type to create a label.</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
